import { useState } from "react";
import type { Character } from "../state/types";
import { ABILITIES, ABILITY_LABEL, sign } from "../state/rowSave";
import { ordinal, slotView } from "../state/spellSlots";
import { SKILLS, checkBonusFor, checkLabel, describeCheckRoll, rollCheck, rollVisibility, type CheckRoll, type CheckSpec, type RollMode, type RollSide, type Visibility } from "../state/checks";

/* The per-card drawer: checks to roll, and what the DM keeps asking a player about.
   Mounted only while open, so a long tracker pays nothing for closed cards.
   The compact SaveRoller on the row is untouched; this is the fuller version. */
export interface DrawerRolled { text: string; meta: string; flat: boolean; visibility: Visibility }

const SPECS: { group: string; items: CheckSpec[] }[] = [
  { group: "Saving throws", items: ABILITIES.map((k) => ({ kind: "save" as const, key: k })) },
  { group: "Ability checks", items: ABILITIES.map((k) => ({ kind: "ability" as const, key: k })) },
  { group: "Skills", items: SKILLS.map((k) => ({ kind: "skill" as const, key: k })) },
];
const idOf = (s: CheckSpec) => s.kind + ":" + s.key;

/** A small three-column grid of spell slots: ●●●○ per level, spent ones hollow. Read from the sheet, so a
 *  slot a player quietly ticked off shows here too. Shown only for a character that has slots at all. */
function SlotTable({ c, onSlot }: { c: Character; onSlot?: (character: Character, level: number, used: number) => Promise<string | void> }) {
  const v = slotView(c);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const canWrite = Boolean(onSlot) && c.id.startsWith("ddb-") && v.known;
  // A click spends one more slot at that level (a free pip) or gives one back (a spent pip). The sheet is the
  // truth: the table changes only after the connector has read the record back.
  const click = async (level: number, raw: number, spend: boolean) => {
    if (!onSlot || busy) return;
    setBusy(String(level)); setErr("");
    const problem = await onSlot(c, level, Math.max(0, spend ? raw + 1 : raw - 1));
    setBusy(""); if (problem) setErr(problem);
  };
  if (!v.rows.length && !v.pact) return null;
  return (
    <div className="slot-table" role="group" aria-label={`Spell slots for ${c.name}`}>
      {v.rows.map((r) => (
        <span key={r.level} className="slot-cell" title={`${ordinal(r.level)}-level slots: ${r.max - r.used} of ${r.max} left${v.known ? "" : " (maximum unknown)"}`}>
          <b>{ordinal(r.level)}</b>{" "}
          {Array.from({ length: r.max }, (_, i) => canWrite
            ? <button key={i} type="button" className={`slot-pip ${i < r.used ? "spent" : "free"}`} disabled={Boolean(busy)} aria-label={i < r.used ? `Give back a ${ordinal(r.level)}-level slot` : `Spend a ${ordinal(r.level)}-level slot`} title={i < r.used ? "Spent. Click to give one back (writes the player's sheet)" : "Free. Click to spend one (writes the player's sheet)"} onClick={() => void click(r.level, r.raw, i >= r.used)}>{i < r.used ? "○" : "●"}</button>
            : <i key={i} className={i < r.used ? "spent" : "free"} aria-hidden="true">{i < r.used ? "○" : "●"}</i>)}
          <span className="sr-only"> {r.max - r.used} of {r.max} left</span>
        </span>
      ))}
      {v.pact && <span className="slot-cell" title="Warlock pact slots; the sheet does not report how many are spent here"><b>Pact</b> {v.pact.count}× {ordinal(v.pact.level)}</span>}
      {busy && <span className="muted small slot-note" role="status">Writing to the sheet…</span>}
      {err && <span className="slot-note slot-err small" role="alert">{err}</span>}
      {!v.known && <span className="muted small slot-note">maximum not known (class unreadable): only spent slots are listed</span>}
    </div>
  );
}

export function CardDrawer({ name, character, side, hideAll, canPost, onRolled, onSlot }: {
  name: string; character: Character | null | undefined; side: RollSide; hideAll: boolean; canPost: boolean;
  onRolled: (r: DrawerRolled) => void;
  onSlot?: (character: Character, level: number, used: number) => Promise<string | void>;
}) {
  const [spec, setSpec] = useState<CheckSpec>({ kind: "save", key: "dex" });
  const [mode, setMode] = useState<RollMode>("normal");
  const [dc, setDc] = useState("");
  const [extra, setExtra] = useState(0);
  const [last, setLast] = useState<{ roll: CheckRoll; text: string } | null>(null);
  const bonus = checkBonusFor(character, spec, extra);
  const visibility = rollVisibility(side, hideAll);
  const roll = () => {
    const r = rollCheck(bonus, mode, dc.trim() === "" ? undefined : Number(dc));
    const text = describeCheckRoll(name, spec, r);
    setLast({ roll: r, text });
    onRolled({ text, meta: r.why, flat: r.basis === "flat", visibility });
  };
  const c = character;
  const speed = c?.movement?.length ? c.movement.map((m) => `${m.type} ${m.feet}`).join(", ") : "";
  const vision = c?.vision?.length ? c.vision.map((v) => `${v.type} ${v.range}`).join(", ") : "";
  return (
    <div className="card-drawer" onClick={(ev) => ev.stopPropagation()}>
      <div className="drawer-checks" role="group" aria-label={`Checks for ${name}`}>
        <select value={idOf(spec)} aria-label={`Check to roll for ${name}`} onChange={(ev) => { const [kind, key] = ev.target.value.split(":"); setSpec({ kind: kind as CheckSpec["kind"], key }); setLast(null); }}>
          {SPECS.map((g) => <optgroup key={g.group} label={g.group}>{g.items.map((s) => <option key={idOf(s)} value={idOf(s)}>{checkLabel(s)}</option>)}</optgroup>)}
        </select>
        <span className="drawer-mode" role="radiogroup" aria-label="Advantage">
          {(["normal", "advantage", "disadvantage"] as RollMode[]).map((m) => (
            <button key={m} role="radio" aria-checked={mode === m} className={`ghost small ${mode === m ? "on" : ""}`} onClick={() => setMode(m)}>{m === "normal" ? "norm" : m === "advantage" ? "adv" : "dis"}</button>
          ))}
        </span>
        <label className="small">DC <input className="xs" type="number" value={dc} onChange={(ev) => setDc(ev.target.value)} aria-label="Difficulty class (optional)" /></label>
        <button className="link small" aria-label="Lower the extra bonus" onClick={() => setExtra((n) => n - 1)}>−</button>
        <span className={`save-bonus basis-${bonus.basis}`} title={bonus.why}>{sign(bonus.bonus)}</span>
        <button className="link small" aria-label="Raise the extra bonus" onClick={() => setExtra((n) => n + 1)}>+</button>
        <button className="small primary" onClick={roll}>Roll</button>
        <span className="muted small" title={canPost ? "Where the result is posted in Roll20 chat" : "Roll20 chat is not connected; results stay in the activity rail"}>
          {canPost ? (visibility === "gm" ? "🔒 GM only" : "📢 table") : "activity rail only"}
        </span>
      </div>
      {last && <div className={`drawer-result ${last.roll.passed === undefined ? "" : last.roll.passed ? "pass" : "fail"}`} role="status">{last.text}</div>}
      {c && (
        <div className="drawer-senses muted small">
          <span>Passive Perc {c.passivePerception} · Ins {c.insight} · Inv {c.investigation}</span>
          {speed && <span> · Speed {speed}</span>}
          {vision && <span> · {vision}</span>}
          {c.ac !== undefined && <span> · AC {c.ac}</span>}
          {Number(c.spellSaveDc) > 0 && <span> · Spell DC {c.spellSaveDc}</span>}
        </div>
      )}
      {c && <SlotTable c={c} onSlot={onSlot} />}
      {!c && <div className="drawer-senses muted small">No sheet: a plain d20 plus your bonus. {ABILITY_LABEL.str} through {ABILITY_LABEL.cha} come from the creature's stat block once HP/AC are wired in.</div>}
    </div>
  );
}
